import { useEffect, useState } from "react";
import { format, isSameDay, isToday } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/cn";
import { WEEK_ABBR, activeApptsOnDay, type WeekAppointment } from "@/lib/dashboard-week";
import { ApptDayCard } from "./ApptDayCard";
import { AppointmentPill } from "./AppointmentPill";

/** Passando disso a célula vira parede de texto e some a leitura; o resto está a um clique, nos
 *  cartões abaixo. */
const MAX_PILULAS = 5;

const rotuloDeConsultas = (n: number) =>
  n === 0 ? "sem consultas" : n === 1 ? "1 consulta" : `${n} consultas`;

/**
 * Visão "Semana": os sete dias viram um SELETOR, e abaixo aparecem as consultas do dia escolhido
 * como cartão.
 *
 * Antes a semana era o calendário do mês em versão menor — mesma grade, sete células em vez de
 * trinta. Não respondia nem "como está a semana" nem "o que tem nesse dia" (faltava espaço pro
 * detalhe). Separando os dois papéis: a faixa responde ao primeiro com as MESMAS pílulas da agenda,
 * os cartões respondem ao segundo.
 *
 * Só roda no desktop — abaixo de 768px o Início troca este card inteiro pela agenda mobile de
 * verdade (ver `Inicio.tsx`), então aqui não há caso de célula estreita a tratar.
 */
export function SemanaComDiaSelecionado({
  days,
  appts,
  onSelectAppt,
}: {
  days: Date[];
  appts: WeekAppointment[];
  onSelectAppt: (appt: WeekAppointment) => void;
}) {
  // Abre no hoje quando a semana é a atual; senão, no primeiro dia que tem consulta — abrir num
  // dia vazio faria a visão parecer sem dados.
  const escolhaInicial = () =>
    days.find((d) => isToday(d)) ?? days.find((d) => activeApptsOnDay(appts, d).length > 0) ?? days[0];

  const [selecionado, setSelecionado] = useState<Date>(escolhaInicial);
  useEffect(() => {
    setSelecionado((atual) => (days.some((d) => isSameDay(d, atual)) ? atual : escolhaInicial()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days[0]?.toISOString(), appts.length]);

  const doDia = activeApptsOnDay(appts, selecionado);
  const doDiaNaFaixa = days.map((d) => activeApptsOnDay(appts, d));
  const totais = doDiaNaFaixa.map((l) => l.length);

  return (
    <div className="w-full">
      <div className="grid grid-cols-7 gap-1">
        {days.map((d, i) => {
          const total = totais[i];
          const ativo = isSameDay(d, selecionado);
          const hoje = isToday(d);
          return (
            <button
              key={d.toISOString()}
              type="button"
              onClick={() => setSelecionado(d)}
              aria-pressed={ativo}
              aria-label={`${format(d, "EEEE, d 'de' MMMM", { locale: ptBR })} — ${rotuloDeConsultas(total)}`}
              title={rotuloDeConsultas(total)}
              className={cn(
                "flex min-h-24 flex-col items-center gap-1 rounded-lg border p-1.5 text-zinc-600 transition-colors dark:text-muted-foreground",
                // Seleção marcada por anel, não por bloco azul sólido: as pílulas aqui dentro são
                // coloridas pelo procedimento, e um fundo cheio apagaria justamente elas.
                ativo
                  ? "border-brand-600 bg-brand-50/70 ring-1 ring-brand-600 dark:border-primary dark:bg-primary/15 dark:ring-primary"
                  : hoje
                    ? "border-brand-200 bg-brand-50/40 hover:bg-brand-50 dark:border-primary/30 dark:bg-primary/10"
                    : "border-zinc-200 hover:bg-zinc-50 dark:border-border dark:hover:bg-muted/40",
              )}
            >
              <span className="text-[10px] font-bold uppercase tracking-wide opacity-80">
                {WEEK_ABBR[d.getDay()]}
              </span>
              <span
                className={cn(
                  "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm tabular-nums",
                  hoje ? "bg-brand-600 font-bold text-white" : "font-semibold text-zinc-700 dark:text-foreground/80",
                )}
              >
                {format(d, "d")}
              </span>

              {/* As mesmas pílulas da agenda da semana, aqui só como leitura — o clique de verdade
                  está nos cartões abaixo, e botão dentro de botão seria HTML inválido. */}
              <span className="flex w-full flex-1 flex-col gap-0.5 pt-0.5">
                {doDiaNaFaixa[i].slice(0, MAX_PILULAS).map((a) => (
                  <AppointmentPill key={a.id} appt={a} />
                ))}
                {/* Não é link nem botão de propósito: clicar em qualquer ponto da célula já escolhe
                    o dia, e a lista completa aparece nos cartões logo abaixo. */}
                {total > MAX_PILULAS && (
                  <span className="pt-0.5 text-center text-[10px] font-semibold text-brand-700 dark:text-primary">
                    Ver mais
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-400 dark:text-muted-foreground">
          {format(selecionado, "EEEE, d 'de' MMMM", { locale: ptBR })}
          {/* Só quando há o que contar — no dia vazio o próprio estado vazio abaixo já diz isso. */}
          {doDia.length > 0 && (
            <span className="ml-2 font-medium normal-case tracking-normal text-zinc-500 dark:text-muted-foreground">
              {rotuloDeConsultas(doDia.length)}
            </span>
          )}
        </p>
        {doDia.length === 0 ? (
          <p className="rounded-lg border border-dashed border-zinc-200 py-6 text-center text-sm text-zinc-500 dark:border-border dark:text-muted-foreground">
            Nenhuma consulta nesse dia.
          </p>
        ) : (
          <div className="space-y-2">
            {doDia.map((a) => (
              <ApptDayCard key={a.id} appt={a} onSelect={onSelectAppt} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
